import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar, Eye, Flame, Loader2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AdSpace from '@/components/AdSpace';
import SubscribeBox from '@/components/SubscribeBox';
import HotTopics from '@/components/HotTopics';
import SEO from '@/components/SEO';
import { getArticles, incrementPageViews } from '@/lib/api';
import { PostPayPromoCard } from '@/components/PostPayPromoCard';
import { getEasternTodayKey } from '@/lib/storyDates';

const BATCH_SIZE = 12;

const toEasternKey = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const formatDayLabel = (dateKey) => {
  const today = new Date().toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  if (dateKey === today) return 'Today';
  if (dateKey === yesterday) return 'Yesterday';

  const [month, day, year] = dateKey.split('/');
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

const StoryDayGrid = ({ group, showPostPay }) => {
  const isToday = group.key === getEasternTodayKey();

  return (
  <section
    className="mb-8"
    data-testid={isToday ? 'today-stories-group' : `allstories-day-group-${group.key.replace(/\//g, '-')}`}
  >
    <div
      className="flex items-center gap-2 mb-3 py-2 border-b border-gray-200"
      data-testid={`allstories-day-header-${group.key.replace(/\//g, '-')}`}
    >
      <Calendar size={14} className="text-yellow-500" />
      <span className="text-sm font-bold text-gray-900">{group.label}</span>
      <span className="text-[10px] text-gray-500 ml-auto">
        {group.articles.length} {group.articles.length === 1 ? 'story' : 'stories'}
      </span>
    </div>

    <div className="grid grid-cols-3 gap-1.5 sm:gap-3 lg:gap-4" data-testid={`allstories-day-grid-${group.key.replace(/\//g, '-')}`}>
      {group.articles.map((article) => (
        <Link
          key={article.id}
          to={`/article/${article.slug || article.id}`}
          className="group"
          data-testid={`allstories-card-${article.id}`}
        >
          <article className="h-full overflow-hidden bg-white border border-gray-200 hover:border-yellow-400 shadow-sm hover:shadow-md transition-[transform,border-color,box-shadow] duration-300 group-hover:-translate-y-1">
            {article.featured_image && (
              <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                <img
                  src={article.featured_image}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
            )}
            <div className="p-1 sm:p-2 lg:p-3">
              <h3 className="text-[10px] sm:text-xs md:text-sm font-semibold leading-tight text-gray-900 line-clamp-2 group-hover:text-yellow-600 transition-colors">
                {article.title}
              </h3>
              <div className="mt-1 flex items-center justify-between gap-1 text-[8px] text-gray-500 sm:mt-2 sm:text-[10px] lg:text-[11px]">
                <span>
                  {article.published_at ? format(new Date(article.published_at), 'MMM d, yyyy') : 'Draft'}
                </span>
                <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                  <Eye size={8} /> {article.views || 0}
                </span>
              </div>
            </div>
          </article>
        </Link>
      ))}
      {isToday && showPostPay && <PostPayPromoCard />}
    </div>
  </section>
  );
};

export default function AllStoriesPage() {
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await getArticles({ category: 'Check On Yo Peoples News Feed', limit: 500 });
        const sorted = [...response.data].sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
        setAllArticles(sorted);
        setVisibleCount(BATCH_SIZE);
      } catch (error) {
        console.error('Failed to fetch all stories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
    incrementPageViews('allstories').catch(() => {});
  }, []);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + BATCH_SIZE, allArticles.length));
  }, [allArticles.length]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  const visibleArticles = allArticles.slice(0, visibleCount);
  const hasMore = visibleCount < allArticles.length;
  const todayKey = getEasternTodayKey();
  const totalTodayStories = allArticles.filter((article) => article.published_at && toEasternKey(article.published_at) === todayKey).length;
  const visibleTodayStories = visibleArticles.filter((article) => article.published_at && toEasternKey(article.published_at) === todayKey).length;
  const showPostPayToday = totalTodayStories > 0 && visibleTodayStories === totalTodayStories;

  const dayGroups = useMemo(() => {
    const groups = [];
    let currentDay = null;

    visibleArticles.forEach((article) => {
      const key = article.published_at ? toEasternKey(article.published_at) : 'Unknown';
      if (key !== currentDay) {
        currentDay = key;
        groups.push({ key, label: formatDayLabel(key), articles: [] });
      }
      groups[groups.length - 1].articles.push(article);
    });

    return groups;
  }, [visibleArticles]);

  return (
    <div className="min-h-screen bg-[#F9FAFB] page-enter" data-testid="allstories-page">
      <SEO title="COYP News Feed" description="All Check On Yo Peoples News Feed stories from Check On Yo Peoples TV, grouped by day in a three-column grid." />
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="mb-6 py-6 border border-gray-200 bg-white animate-fade-in-up text-center" data-testid="allstories-hero">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Flame className="text-yellow-500" size={26} />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="allstories-page-title">CHECK ON YO PEOPLES NEWS FEED</h1>
            <Flame className="text-yellow-500" size={26} />
          </div>
          <p className="text-gray-600 text-sm" data-testid="allstories-page-description">Every COYP News Feed story, grouped by day</p>
        </div>

        <AdSpace type="header" className="mb-6" />

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
          </div>
        ) : dayGroups.length > 0 ? (
          <>
            {dayGroups.map((group) => (
              <StoryDayGrid key={group.key} group={group} showPostPay={showPostPayToday} />
            ))}

            {hasMore && (
              <div ref={sentinelRef} className="flex items-center justify-center py-8" data-testid="allstories-load-more-sentinel">
                <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20 border border-gray-200 bg-white" data-testid="allstories-empty-state">
            <p className="text-gray-600">No COYP News Feed stories yet.</p>
          </div>
        )}

        <AdSpace type="footer" className="mt-6" />
        <HotTopics theme="light" />
        <HotTopics theme="light" period="last_week" heading="Last Week's Hottest" />
        <div className="mt-6 mb-8">
          <SubscribeBox theme="light" />
        </div>
      </main>

      <Footer theme="light" />
    </div>
  );
}